// Runtime values and the objects that live in the MVCC store.

import type { LambdaExpr } from "../../frontend/ast.js";
import type { RuntimeIterator } from "./collection-iter.js";

/** An address is a path key like "/1" (object) or "/4/2" (set entry). */
export type Address = string;

/** The captured lexical environment of a closure — just enough of the
 *  interpreter's `Scope` to read a variable. A `Scope` satisfies it structurally,
 *  so a closure can hold the scope it was created in without this leaf module
 *  depending on the interpreter. */
export interface ClosureEnv {
  get(name: string): Value | undefined;
  has(name: string): boolean;
}

export type Value =
  | { kind: "int"; value: number }
  | { kind: "float"; value: number }
  | { kind: "bool"; value: boolean }
  | { kind: "string"; value: string }
  // A single Unicode code point (`value` is the code point number). Inline and
  // immutable like the other scalars; compared/ordered by code point.
  | { kind: "char"; value: number }
  | { kind: "ptr"; addr: Address }
  | { kind: "enum"; enumName: string; variant: string; fields: Record<string, Value> }
  // A **value type** instance (a struct whose every field is `key`; see
  // `isValueType`). Unlike a `ptr` struct it lives *inline* — it never gets its
  // own address in the store — so it is immutable and compared/hashed by its
  // fields, not by identity. `fields` are in declared order (scalars or nested
  // records only), which is what `keySegment`/`valuesEqual` rely on.
  | { kind: "record"; typeName: string; fields: Record<string, Value> }
  | { kind: "void" }
  // A first-class function value: a reference to a top-level function by `name`.
  // Functions aren't closures (they capture nothing), so the name is all that's
  // needed — calling it looks the declaration up and runs it with fresh scope.
  | { kind: "fn"; name: string }
  // A closure: an anonymous function (`fn`, the lambda's AST) paired with the
  // environment it captured at creation (`env`). Calling it binds the parameters
  // in a fresh scope nested in `env`, then evaluates the body expression.
  | { kind: "closure"; fn: LambdaExpr; env: ClosureEnv }
  // A built-in collection's iterator cursor — the value `Set.iter()`/`Dict.iter()`
  // hand back (the STM types `SetIter<T>`/`DictIter<K, V>`). `iter` is the native
  // driver (a snapshot of the collection's entries plus a position); `next()` on it
  // yields `Some`/`None`. `typeName` is the STM iterator type, so it dispatches the
  // `Iterator` protocol (this is how a set/dict flows through `map`/`filter`). It is
  // never stored in the MVCC store — a transient cursor, like a `closure`.
  | { kind: "collIter"; typeName: string; iter: RuntimeIterator<never> };

/** What is stored at a single address. */
export type StoredObject =
  | { kind: "struct"; typeName: string; fields: Record<string, Value> }
  // Every collection — Set, Dict, List — shares one representation: a
  // namespace *marker* plus one child *entry* cell per element, keyed under the
  // marker's address (/coll/<key>). Each entry is its own versioned,
  // copy-on-write cell, so membership participates in MVCC per entry and the
  // marker itself is written once and never rewritten. `container` names the
  // kind ("Set"/"Dict") and `typeArgs` records its element types for display.
  | { kind: "collection"; container: string; typeArgs: string[] }
  // One collection entry. `key` is the value the entry is keyed by (a pointer
  // for object keys, or a primitive — so primitives, which have no address, can
  // be keys too); `value` is the associated payload. A Set stores `value: VOID`
  // (membership only); a Dict stores the mapped value; a List keys by index.
  // Keeping one shape means add/get/remove and iteration are shared across all
  // three containers.
  | { kind: "entry"; key: Value; value: Value }
  // A collection's KEY-SET version, stored at a sibling address (/coll#keys).
  // Iteration and size READ it; inserting/removing a key BUMPS it (a blind
  // write). So a range read is conflict-detected against a concurrent structural
  // change to keys it couldn't have seen — making iteration/size serializable.
  // The value carries nothing; the store's own version is the whole point.
  | { kind: "keyset" };

/** A short type name for a stored object, for traces / debug labels: a struct's
 *  declared type, a collection's container kind ("Set"/"Dict"), or "entry" for a
 *  single collection cell (whose type is better read from its parent collection). */
export function storedTypeName(obj: StoredObject): string {
  switch (obj.kind) {
    case "struct":
      return obj.typeName;
    case "collection":
      return obj.container;
    case "entry":
      return "entry";
    case "keyset":
      return "keyset";
  }
}

export const VOID: Value = { kind: "void" };

export function int(value: number): Value {
  return { kind: "int", value: Math.trunc(value) };
}
export function float(value: number): Value {
  return { kind: "float", value };
}
export function bool(value: boolean): Value {
  return { kind: "bool", value };
}
export function str(value: string): Value {
  return { kind: "string", value };
}
/** A char value from a Unicode code point. */
export function char(value: number): Value {
  return { kind: "char", value: Math.trunc(value) };
}
/** The one-character string a char renders to (its `Show`/`str` form). */
export function charString(codePoint: number): string {
  return String.fromCodePoint(codePoint);
}
export function ptr(addr: Address): Value {
  return { kind: "ptr", addr };
}

export function isNumeric(v: Value): v is { kind: "int" | "float"; value: number } {
  return v.kind === "int" || v.kind === "float";
}

export function truthy(v: Value): boolean {
  switch (v.kind) {
    case "bool":
      return v.value;
    case "int":
    case "float":
      return v.value !== 0;
    case "string":
      return v.value.length > 0;
    case "char":
      return true;
    case "void":
      return false;
    default:
      return true;
  }
}

export function valuesEqual(a: Value, b: Value): boolean {
  if (isNumeric(a) && isNumeric(b)) return a.value === b.value;
  if (a.kind !== b.kind) return false;
  switch (a.kind) {
    case "bool":
      return a.value === (b as typeof a).value;
    case "string":
      return a.value === (b as typeof a).value;
    case "char":
      return a.value === (b as typeof a).value;
    case "ptr":
      return a.addr === (b as typeof a).addr;
    case "void":
      return true;
    case "fn":
      return a.name === (b as typeof a).name;
    case "closure":
      // Closures are reference-identity values (each lambda evaluation makes a
      // fresh one); two are equal only when they're the same object.
      return a === b;
    case "enum": {
      const be = b as typeof a;
      if (a.enumName !== be.enumName || a.variant !== be.variant) return false;
      const ak = Object.keys(a.fields);
      if (ak.length !== Object.keys(be.fields).length) return false;
      return ak.every((k) => valuesEqual(a.fields[k]!, be.fields[k]!));
    }
    case "record": {
      // Value types are equal when their type and every field are equal — a
      // structural, by-value comparison (contrast `ptr`, compared by address).
      const br = b as typeof a;
      if (a.typeName !== br.typeName) return false;
      const ak = Object.keys(a.fields);
      if (ak.length !== Object.keys(br.fields).length) return false;
      return ak.every((k) => valuesEqual(a.fields[k]!, br.fields[k]!));
    }
    default:
      return false;
  }
}

/**
 * A single, path-safe key segment identifying `v` inside a Set/Dict namespace.
 *
 * This is what lets a collection be keyed by EITHER an object OR a primitive:
 * every value maps to one segment, so `add`/`set`/`get`/`contains` each touch
 * exactly one child key (`/coll/<segment>`) and get per-entry MVCC for free.
 *
 * The encoding is collision-free across kinds (each kind has a distinct prefix)
 * and agrees with `valuesEqual`: numbers share one prefix so `1` (int) and `1.0`
 * (float) map to the same key, mirroring numeric equality; strings are
 * percent-encoded so they can never introduce a `/` and split the path. Object
 * pointers are top-level addresses (`/n`), so their id is already a lone segment.
 */
export function keySegment(v: Value): string {
  switch (v.kind) {
    case "int":
    case "float":
      return "n" + String(v.value);
    case "bool":
      return "b" + (v.value ? "1" : "0");
    case "string":
      return "s" + encodeURIComponent(v.value);
    case "char":
      return "c" + v.value;
    case "ptr":
      return "o" + v.addr.slice(1);
    case "enum":
      // An enum is an inline, immutable value, so it keys by value like a record:
      // tag by enum + variant, then fold the variant's fields (declared order)
      // into the segment. A field that is itself a value (scalar / record / enum)
      // hashes by value; a `ptr` field hashes by identity — mirroring how a
      // struct key works.
      return (
        "e" +
        encodeURIComponent(v.enumName) +
        "." +
        encodeURIComponent(v.variant) +
        "(" +
        Object.values(v.fields).map(keySegment).join(",") +
        ")"
      );
    case "record":
      // A value type hashes by its fields, not by identity: recurse over the
      // fields (declared order) and fold them into one path-safe segment. The
      // type name is percent-encoded and the field segments are `/`-free
      // already, so `r<Type>(seg,seg,…)` never introduces a path separator and
      // two structurally-equal records map to the same key (mirroring
      // `valuesEqual`).
      return (
        "r" +
        encodeURIComponent(v.typeName) +
        "(" +
        Object.values(v.fields).map(keySegment).join(",") +
        ")"
      );
    default:
      throw new Error(`value of kind \`${v.kind}\` cannot be a collection key`);
  }
}

/** Render a value for traces / debug output. Uses the store to expand pointers. */
export function displayValue(
  v: Value,
  resolve?: (addr: Address) => StoredObject | undefined,
  depth = 0
): string {
  switch (v.kind) {
    case "int":
      return String(v.value);
    case "float":
      return Number.isInteger(v.value) ? v.value.toFixed(1) : String(v.value);
    case "bool":
      return String(v.value);
    case "string":
      return JSON.stringify(v.value);
    case "char":
      return "'" + String.fromCodePoint(v.value) + "'";
    case "void":
      return "()";
    case "fn":
      return `<fn ${v.name}>`;
    case "closure":
      return "<closure>";
    case "enum": {
      const fields = Object.entries(v.fields)
        .map(([k, fv]) => `${k}: ${displayValue(fv, resolve, depth + 1)}`)
        .join(", ");
      return fields ? `${v.variant}(${fields})` : `${v.variant}`;
    }
    case "record": {
      // Rendered like a struct literal — value types read the same as the heap
      // structs they mirror, just without an address.
      const fields = Object.entries(v.fields)
        .map(([k, fv]) => `${k}: ${displayValue(fv, resolve, depth + 1)}`)
        .join(", ");
      return `${v.typeName}{${fields}}`;
    }
    case "ptr": {
      if (!resolve || depth > 3) return v.addr;
      const obj = resolve(v.addr);
      if (!obj) return `${v.addr}<dangling>`;
      if (obj.kind === "struct") {
        const fields = Object.entries(obj.fields)
          .map(([k, fv]) => `${k}: ${displayValue(fv, resolve, depth + 1)}`)
          .join(", ");
        return `${obj.typeName}{${fields}}`;
      }
      if (obj.kind === "collection") return `${obj.container}<${obj.typeArgs.join(", ")}>@${v.addr}`;
      return v.addr;
    }
    case "collIter":
      return `<${v.typeName}>`;
  }
}
